// ────────────────────────────────────────────────────────────────────────────
// Settings → Trip Checkpoints.
//
// Three panes: the templates, the one being edited, and what a real order
// would get from ALL of them together. The preview is the point — a template
// is only right if the assembled list on a real job reads right — so it is
// assembled by the server, with the template being edited standing in for its
// saved copy, from the same code that will freeze the copy sent to a driver.
//
// Edits collect in a working copy; Save writes every changed template (create
// or whole-object update) and Discard goes back to what is stored. Deleting a
// saved template asks first and happens at once.
//
// Reachable with `settings.tripCheckpoints` — the TMS Mobile module switched
// on, and Owner/Admin. The server checks the same.
// ────────────────────────────────────────────────────────────────────────────
import { useAuth0 } from '@auth0/auth0-react';
import { useAddressesByIds, useParty } from '@my/directory-api/hooks';
import {
  useCargoDefinitions,
  useCreateTripCheckpointTemplate,
  useDeleteTripCheckpointTemplate,
  useOrdersPage,
  useServiceTags,
  useTripCheckpointPreview,
  useTripCheckpointTemplates,
  useUpdateTripCheckpointTemplate,
} from '@my/tms-api/hooks';
import type { TripCheckpointPreviewRequest } from '@my/tms-api';
import { Button, ConfirmDialog, ErrorBanner, Loading, PageHeader, showToast } from '@my/ui';
import { Save, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { OrderPreview, type PreviewNames, type PreviewOrderOption } from './components/OrderPreview';
import { TemplateEditor } from './components/TemplateEditor';
import { TemplateList } from './components/TemplateList';
import { buildCatalogue } from './lib/conditions';
import { isNewId, newTemplate, type CheckpointTemplate } from './lib/model';
import { templateFromWire, templateToFields } from './lib/wire';

/** The value, once it has stopped changing for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

export function TripCheckpointsPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const templatesQuery = useTripCheckpointTemplates(orgId);
  const definitionsQuery = useCargoDefinitions(orgId);
  const tagsQuery = useServiceTags(orgId);
  const createMutation = useCreateTripCheckpointTemplate();
  const updateMutation = useUpdateTripCheckpointTemplate();
  const deleteMutation = useDeleteTripCheckpointTemplate();

  const catalogue = useMemo(
    () => buildCatalogue(definitionsQuery.data ?? [], tagsQuery.data ?? []),
    [definitionsQuery.data, tagsQuery.data],
  );

  // ── The working copy ──────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<CheckpointTemplate[]>([]);
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CheckpointTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-seed from the server whenever it answers and nothing is unsaved — first
  // load, and after a save or delete refetches. Never over the top of edits.
  const stored = templatesQuery.data;
  useEffect(() => {
    if (!stored || dirtyIds.size > 0) return;
    const fresh = stored.map(templateFromWire);
    setTemplates(fresh);
    setSelectedId((id) => (id && fresh.some((t) => t.id === id) ? id : (fresh[0]?.id ?? null)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed on new server data only
  }, [stored]);

  const selected = templates.find((t) => t.id === selectedId) ?? null;
  const dirty = dirtyIds.size > 0;

  const change = (next: CheckpointTemplate) => {
    setTemplates((ts) => ts.map((t) => (t.id === next.id ? next : t)));
    setDirtyIds((ids) => new Set(ids).add(next.id));
  };

  const add = () => {
    const t = newTemplate();
    setTemplates((ts) => [...ts, t]);
    setSelectedId(t.id);
    setDirtyIds((ids) => new Set(ids).add(t.id));
  };

  const dropLocally = (id: string) => {
    const rest = templates.filter((t) => t.id !== id);
    setTemplates(rest);
    setSelectedId((sel) => (sel === id ? (rest[0]?.id ?? null) : sel));
    setDirtyIds((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });
  };

  const confirmDelete = async () => {
    const t = pendingDelete;
    if (!t || !orgId) return;
    try {
      if (!isNewId(t.id)) await deleteMutation.mutateAsync({ organisationId: orgId, id: t.id });
      dropLocally(t.id);
      showToast({ type: 'success', message: `“${t.name || 'Untitled'}” deleted.` });
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Failed to delete the template.' });
    } finally {
      setPendingDelete(null);
    }
  };

  const discard = () => {
    const fresh = (stored ?? []).map(templateFromWire);
    setTemplates(fresh);
    setDirtyIds(new Set());
    setSelectedId((id) => (id && fresh.some((t) => t.id === id) ? id : (fresh[0]?.id ?? null)));
  };

  const save = async () => {
    if (!orgId) return;
    const toSave = templates.filter((t) => dirtyIds.has(t.id));
    const unnamed = toSave.find((t) => !t.name.trim());
    if (unnamed) {
      setSelectedId(unnamed.id);
      showToast({ type: 'error', message: 'Every template needs a name.' });
      return;
    }

    setSaving(true);
    const remaining = new Set(dirtyIds);
    let selectAfter = selectedId;
    try {
      // One at a time, so a failure says exactly which template it was and
      // everything before it is already safe.
      for (const t of toSave) {
        const fields = templateToFields(t);
        if (isNewId(t.id)) {
          const id = await createMutation.mutateAsync({ organisationId: orgId, ...fields });
          if (selectAfter === t.id) selectAfter = id;
        } else {
          await updateMutation.mutateAsync({
            organisationId: orgId,
            body: { tripCheckpointTemplateId: t.id, ...fields },
          });
        }
        remaining.delete(t.id);
      }
      showToast({ type: 'success', message: toSave.length === 1 ? 'Template saved.' : 'Templates saved.' });
      setSelectedId(selectAfter);
    } catch (err) {
      const failed = toSave.find((t) => remaining.has(t.id));
      setSelectedId(failed?.id ?? selectedId);
      showToast({
        type: 'error',
        message: `${failed ? `“${failed.name}”: ` : ''}${err instanceof Error ? err.message : 'Failed to save.'}`,
      });
    } finally {
      // Saved ones are no longer dirty; when nothing is left the refetch
      // re-seeds the working copy with the server's ids.
      setDirtyIds(remaining);
      setSaving(false);
    }
  };

  // ── Preview ───────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search.trim(), 300);
  const ordersQuery = useOrdersPage(
    orgId ? { organisationId: orgId, search: debouncedSearch || undefined, sort: 'newest' } : null,
  );
  const definitionName = (id: string) => catalogue.cargoDefinitions.find((d) => d.id === id)?.name;
  const orderOptions: PreviewOrderOption[] = (ordersQuery.data?.orders ?? []).slice(0, 50).map((o) => ({
    id: o.id,
    label: [o.orderNumber, definitionName(o.cargoDefinitionId)].filter(Boolean).join(' · '),
  }));
  const [pickedOrderId, setPickedOrderId] = useState<string | null>(null);
  const orderId =
    pickedOrderId && orderOptions.some((o) => o.id === pickedOrderId) ? pickedOrderId : (orderOptions[0]?.id ?? null);

  // Only the template being edited goes along as a draft, and only once it
  // differs from what is stored; typing settles before a request is made.
  const draft = selected && dirtyIds.has(selected.id) ? selected : null;
  const previewRequest: TripCheckpointPreviewRequest | null = useMemo(
    () =>
      orgId && orderId
        ? {
            organisationId: orgId,
            orderId,
            draft: draft ? { ...templateToFields(draft), id: isNewId(draft.id) ? null : draft.id } : null,
          }
        : null,
    [orgId, orderId, draft],
  );
  const settledRequest = useDebounced(previewRequest, 400);
  const previewQuery = useTripCheckpointPreview(settledRequest);

  // The customer and the stops by name, from Directory, as the order screens
  // name them — the preview only carries their ids.
  const preview = previewQuery.data;
  const customerQuery = useParty(preview?.facts.customerPartyId ?? null);
  const addressIds = useMemo(
    () => (preview?.tasks ?? []).map((t) => t.addressId).filter((id): id is string => !!id),
    [preview],
  );
  const addressesQuery = useAddressesByIds(orgId, addressIds);
  const names: PreviewNames = useMemo(
    () => ({
      customer: customerQuery.data?.name ?? null,
      places: new Map(
        (addressesQuery.data ?? []).map((a) => [
          a.id,
          [a.name?.trim(), a.city?.trim()].filter(Boolean).join(', ') || a.fullAddress?.trim() || '',
        ]),
      ),
    }),
    [customerQuery.data, addressesQuery.data],
  );

  const loadError = templatesQuery.error ?? definitionsQuery.error ?? tagsQuery.error;

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title="Trip Checkpoints"
        description="What has to be checked on a job, and by whom — the planner in the office, our driver or the subcontractor on site."
      >
        {dirty && (
          <div className="flex items-center gap-1">
            <Button size="xs" variant="ghost" leftIcon={Undo2} onClick={discard} disabled={saving}>
              Discard
            </Button>
            <Button size="xs" leftIcon={Save} onClick={save} loading={saving}>
              Save
            </Button>
          </div>
        )}
      </PageHeader>

      {!orgId && (
        <ErrorBanner
          title="No organisation"
          message="Your account isn't linked to an organisation, so there is nothing to configure here."
        />
      )}
      {loadError && <ErrorBanner title="Couldn't load trip checkpoints" message={loadError.message} />}
      {templatesQuery.isPending && orgId && <Loading label="Loading templates…" />}

      {templatesQuery.isSuccess && (
        <div className="grid grid-cols-1 gap-3 pb-4 lg:grid-cols-[220px_minmax(0,1fr)] 2xl:min-h-0 2xl:flex-1 2xl:grid-cols-[220px_minmax(0,1fr)_380px]">
          <TemplateList
            templates={templates}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAdd={add}
            catalogue={catalogue}
            dirtyIds={dirtyIds}
          />

          <div className="min-w-0 2xl:overflow-auto">
            {selected ? (
              <TemplateEditor
                template={selected}
                onChange={change}
                onDelete={() => setPendingDelete(selected)}
                catalogue={catalogue}
              />
            ) : (
              <p className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                No templates yet. Create one with “New” — for example a Standard template for every order, then
                one per kind of cargo that needs more.
              </p>
            )}
          </div>

          <OrderPreview
            orders={orderOptions}
            orderId={orderId}
            onPick={setPickedOrderId}
            search={search}
            onSearch={setSearch}
            preview={previewQuery.data}
            names={names}
            loading={previewQuery.isFetching}
            error={previewQuery.error}
            className="lg:col-span-2 2xl:col-span-1 2xl:overflow-auto"
          />
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title="Delete template?"
        message={
          pendingDelete
            ? `“${pendingDelete.name || 'Untitled'}” will no longer switch on for any order. Checkpoints already sent to drivers are copies and are not affected.`
            : null
        }
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </div>
  );
}
